import { Injectable } from '@angular/core';
import { ControlValueAccessor } from '@angular/forms';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  debounce,
  filter,
  skip
} from 'rxjs';
//----------------------------------
import { ReactiveComponent } from './reactive-component';

@Injectable()
export abstract class ReactiveControlComponent<T = any>
  extends ReactiveComponent
  implements ControlValueAccessor
{
  private readonly _value$: BehaviorSubject<T>;
  readonly value$: Observable<T>;
  readonly valueWithDebounce$: Observable<T>;

  private readonly _controlWritten$ = new BehaviorSubject(false);
  readonly controlWritten$ = this._controlWritten$.asObservable();
  readonly controlWrittenDebounce = <K>() =>
    debounce<K>(() => this.controlWritten$.pipe(filter((v) => v)));

  constructor() {
    super();
    this._value$ = new BehaviorSubject(this.getInitialValue());
    this.value$ = this._value$.asObservable();
    this.valueWithDebounce$ = this._value$
      .asObservable()
      .pipe(this.controlWrittenDebounce());
  }

  protected getInitialValue(): T {
    return null;
  }

  get value(): T | null {
    return this._value$.value;
  }

  set value(value: T) {
    this._value$.next(value);
  }

  get controlWritten() {
    return this._controlWritten$.value;
  }

  private _onChange: (value: T) => void = () => {};
  private _onTouched = () => {};

  private readonly _touched$ = new BehaviorSubject(false);

  get touched(): boolean {
    return this._touched$.value;
  }

  set touched(value: boolean) {
    this._touched$.next(value);
  }

  private readonly _disabled$ = new BehaviorSubject(false);
  readonly disabled$ = this._disabled$.asObservable();

  get disabled(): boolean {
    return this._disabled$.value;
  }

  *onSubscribe(): Iterable<Subscription> {
    yield this._value$
      .pipe(skip(1))
      .subscribe((value) => this._onChange(value));
    yield this._touched$.subscribe((value) => !!value && this._onTouched());
  }

  writeValue(obj: T | null): void {
    this._value$.next(obj);
    this._controlWritten$.next(true);
  }

  registerOnChange(fn: (value: T) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this._disabled$.next(isDisabled);
  }
}
